import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Loader2, ChevronRight, ArrowLeft, FileSignature, FileX2 } from "lucide-react";
import api, { fmtIDR, fmtDate } from "@/lib_app/api";
import { shortProjectName } from "@/components/app/InvoiceDetailModal";

/**
 * Two-step picker for a new invoice:
 *  1. pick the deal-won project it bills against
 *  2. optionally copy items & terms from one of that project's quotations
 */
export default function ProjectPickerDialog({ open, onClose, onPick }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [project, setProject] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [qLoading, setQLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setProject(null);
    setQuotations([]);
    setLoading(true);
    api.get("/projects")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open]);

  const chooseProject = async (p) => {
    setProject(p);
    if (!p.prospect_id) { setQuotations([]); return; }
    setQLoading(true);
    try {
      const r = await api.get("/quotations", { params: { prospect_id: p.prospect_id } });
      setQuotations(r.data || []);
    } catch {
      setQuotations([]);
    } finally {
      setQLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((p) =>
      (p.customer || "").toLowerCase().includes(s) ||
      (p.name || "").toLowerCase().includes(s) ||
      (p.service || "").toLowerCase().includes(s) ||
      (p.sales_name || "").toLowerCase().includes(s));
  }, [rows, q]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-xl rounded-3xl border-0 p-0 overflow-hidden"
        data-testid="project-picker-dialog"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.97) 0%, rgba(232,240,254,0.94) 100%)",
          boxShadow: "0 32px 80px -20px rgba(59,74,99,0.30)",
        }}
      >
        <DialogTitle className="sr-only">Pilih Proyek</DialogTitle>
        <DialogDescription className="sr-only">Pilih proyek untuk invoice baru</DialogDescription>

        {!project ? (
          <>
            <div className="p-6 pb-3">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold mb-1">Invoice Baru</p>
              <h2 className="text-xl font-bold text-[#090808]">Pilih Proyek</h2>
              <p className="text-xs text-[#A0A3A8] mt-1">Invoice hanya bisa dibuat dari proyek yang sudah deal.</p>
              <div className="relative mt-4">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#A0A3A8]" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="project-picker-search"
                  placeholder="Cari proyek / pelanggan / sales..."
                  className="w-full h-11 pl-11 pr-3 rounded-full bg-white border border-[#DCE0E7] text-sm outline-none focus:ring-2 focus:ring-[#97ADD4]/40"
                />
              </div>
            </div>

            <div className="px-4 pb-5 max-h-[52vh] overflow-y-auto">
              {loading ? (
                <div className="py-12 text-center text-[#A0A3A8]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
              ) : filtered.length === 0 ? (
                <div className="py-12 text-center text-sm text-[#A0A3A8]" data-testid="project-picker-empty">Belum ada proyek deal.</div>
              ) : (
                <div className="space-y-2">
                  {filtered.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => chooseProject(p)}
                      data-testid={`project-picker-item-${p.id}`}
                      className="w-full text-left rounded-2xl bg-white border border-[#DCE0E7]/70 px-4 py-3 flex items-center gap-3 hover:shadow-md hover:border-[#B0C7EA] transition"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-[#090808] truncate">{p.customer}</div>
                        <div className="text-xs text-[#626061] truncate">{shortProjectName(p.name, p.customer) || p.service || "-"}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-sm font-num font-bold text-[#3B4A63] tabular-nums">{fmtIDR(p.total_value)}</div>
                        <div className="text-[10px] uppercase tracking-widest text-[#A0A3A8] font-bold">{p.sales_nickname || p.sales_name || "-"}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#A0A3A8] shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="p-6 pb-3">
              <button
                onClick={() => setProject(null)}
                data-testid="project-picker-back"
                className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-[#3B4A63] hover:underline mb-3"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Ganti Proyek
              </button>
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#6B8FE2] font-bold mb-1">{project.customer}</p>
              <h2 className="text-xl font-bold text-[#090808]">Sumber Item Invoice</h2>
              <p className="text-xs text-[#A0A3A8] mt-1">Salin item &amp; syarat dari quotation, atau mulai dari nilai proyek.</p>
            </div>

            <div className="px-4 pb-5 max-h-[52vh] overflow-y-auto space-y-2">
              {qLoading ? (
                <div className="py-10 text-center text-[#A0A3A8]"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
              ) : (
                <>
                  {quotations.map((qt) => (
                    <button
                      key={qt.id}
                      onClick={() => onPick({ project, quotationId: qt.id })}
                      data-testid={`project-picker-quotation-${qt.id}`}
                      className="w-full text-left rounded-2xl bg-white border border-[#DCE0E7]/70 px-4 py-3 flex items-center gap-3 hover:shadow-md hover:border-[#B0C7EA] transition"
                    >
                      <div className="w-9 h-9 rounded-full bg-[#E5EEFA] text-[#3B4A63] flex items-center justify-center shrink-0">
                        <FileSignature className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-bold text-[#090808] font-num tabular-nums truncate">{qt.number}</div>
                        <div className="text-xs text-[#626061] truncate">{fmtDate(qt.date)} · {qt.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}</div>
                      </div>
                      <div className="text-sm font-num font-bold text-[#3B4A63] tabular-nums shrink-0">{fmtIDR(qt.grand_total)}</div>
                      <ChevronRight className="w-4 h-4 text-[#A0A3A8] shrink-0" />
                    </button>
                  ))}
                  <button
                    onClick={() => onPick({ project, quotationId: null })}
                    data-testid="project-picker-no-quotation"
                    className="w-full text-left rounded-2xl bg-white/70 border border-dashed border-[#B0C7EA] px-4 py-3 flex items-center gap-3 hover:bg-white transition"
                  >
                    <div className="w-9 h-9 rounded-full bg-[#F4F5F7] text-[#626061] flex items-center justify-center shrink-0">
                      <FileX2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-[#090808]">Tanpa Quotation</div>
                      <div className="text-xs text-[#626061]">Pakai nilai proyek {fmtIDR(project.base_value)} sebagai satu item</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#A0A3A8] shrink-0" />
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
